
function isMobileDevice() {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
}

export interface PDMeasurementResult {
    pd: number;
    leftPd?: number;
    rightPd?: number;
}

export interface PDMeasurementState {
    phase: string;
    isFaceStraight: boolean;
    calibrationProgress: number;
    cardDetected: boolean;
}

export interface PDMeasurementConfig {
    pdType?: 'single' | 'dual';
    onStateChange?: (state: PDMeasurementState) => void;
    onStatus: (message: string) => void;
    onDetected: (result: PDMeasurementResult) => void;
    onError: (error: string) => void;
}

export class PDMeasurementLib {
    private video: HTMLVideoElement;
    private config: PDMeasurementConfig;
    private isRunning = false;
    private lastEmittedStateStr: string = '';
    private session: any = null;
    private faceMesh: any = null;
    
    private ctx: CanvasRenderingContext2D;
    
    // Model Config
    private isMobile = isMobileDevice();
    private YOLO_MODEL = this.isMobile ? '/models/glasses_cards_yolov8s_worldv2_640.onnx' : '/models/glasses_cards_yolov8m_worldv2_960.onnx';
    private YOLO_SIZE = this.isMobile ? 640 : 960;
    private selectedCardWidth = 85.60;
    
    // Internal State
    private leftIris: { x: number, y: number } | null = null;
    private rightIris: { x: number, y: number } | null = null;
    private leftInner: { x: number, y: number } | null = null;
    private rightInner: { x: number, y: number } | null = null;
    private isFaceStraight = false;
    
    // Two-Phase Architecture State
    private phase: 'calibration' | 'measurement' = 'calibration';
    
    // Calibration State
    private cardDistance: {minX: number, minY: number, maxX: number, maxY: number, videoWidthPixels?: number} | null = null;
    private foreheadDetected = false;
    private calibrationFrameCount = 0;
    private lastCardWidth: number | undefined = undefined;
    private cardWidthSamples: number[] = [];
    private mmPerPixel: number | null = null;
    
    // Measurement State
    private pdSamples: { total: number, left?: number, right?: number }[] = [];
    
    constructor(video: HTMLVideoElement, config: PDMeasurementConfig) {
        this.config = config;
        this.video = video;
        
        const canvas = document.createElement('canvas');
        canvas.width = this.YOLO_SIZE;
        canvas.height = this.YOLO_SIZE;
        this.ctx = canvas.getContext('2d', { willReadFrequently: true })!;
    }
    
    public async start() {
        if (this.isRunning) return;
        this.isRunning = true;
        this.phase = 'calibration';
        this.calibrationFrameCount = 0;
        this.cardWidthSamples = [];
        this.pdSamples = [];
        this.mmPerPixel = null;
        this.lastCardWidth = undefined;
        
        try {
            if (!this.session || !this.faceMesh) {
                await this.loadModels();
            }
            this.config.onStatus("Card Calibration: Sit straight, center your face, and hold card steady on forehead.");
            this.detectLoop();
        } catch (e: any) {
            this.config.onError(e.message || String(e));
        }
    }
    
    public stop() {
        this.isRunning = false;
        if (this.faceMesh) {
            try { this.faceMesh.close(); } catch(e){}
        }
    }
    
    private async loadModels() {
        this.config.onStatus("Loading...");
        
        const ort = (window as any).ort;
        if (!ort) {
            throw new Error("ort library not loaded from index.html");
        }
        
        ort.env.wasm.simd = true;
        ort.env.wasm.numThreads = 1;
        ort.env.wasm.proxy = false;
        ort.env.wasm.wasmPaths = 'https://cdn.jsdelivr.net/npm/onnxruntime-web@latest/dist/';
        
        const modelBuffer = await fetch(this.YOLO_MODEL).then(r => r.arrayBuffer());
        
        if ((navigator as any).gpu) {
            try {
                const adapter = await (navigator as any).gpu.requestAdapter();
                if (adapter) {
                    const device = await adapter.requestDevice();
                    this.session = await ort.InferenceSession.create(modelBuffer.slice(0), {
                        executionProviders: ['webgpu'],
                        graphOptimizationLevel: 'all',
                        webgpu: { device }
                    });
                }
            } catch (error) {
                console.warn('WebGPU failed, falling back to WASM:', error);
            }
        }
        
        if (!this.session) {
            try {
                this.session = await ort.InferenceSession.create(modelBuffer, {
                    executionProviders: ['wasm'],
                    graphOptimizationLevel: 'all'
                });
            } catch (err) {
                console.error("ORT session creation failed:", err);
                throw err;
            }
        }
        
        this.config.onStatus("Loading...");
        await this.loadFaceModel();
    }
    
    private async loadFaceModel() {
        return new Promise<void>((resolve, reject) => {
            const initFaceMesh = () => {
                const FaceMesh = (window as any).FaceMesh;
                if (!FaceMesh) {
                    return reject(new Error("FaceMesh library failed to load."));
                }
                
                this.faceMesh = new FaceMesh({
                    locateFile: (file: string) => `https://cdn.jsdelivr.net/npm/@mediapipe/face_mesh/${file}`
                });
                
                this.faceMesh.setOptions({
                    maxNumFaces: 1,
                    refineLandmarks: true,
                    minDetectionConfidence: 0.5,
                    minTrackingConfidence: 0.5
                });
                
                this.faceMesh.onResults(this.onFaceResults.bind(this));
                
                // Warm up
                this.faceMesh.initialize().then(() => resolve()).catch(reject);
            };
            
            if ((window as any).FaceMesh) {
                initFaceMesh();
            } else {
                const script = document.createElement('script');
                script.src = 'https://cdn.jsdelivr.net/npm/@mediapipe/face_mesh/face_mesh.js';
                script.crossOrigin = 'anonymous';
                script.onload = () => initFaceMesh();
                script.onerror = () => reject(new Error("Failed to load FaceMesh from CDN."));
                document.head.appendChild(script);
            }
        });
    }
    
    private onFaceResults(results: any) {
        if (results.multiFaceLandmarks && results.multiFaceLandmarks.length > 0) {
            const landmarks = results.multiFaceLandmarks[0];
            const leftEyeIndices = [468, 469, 470, 471, 472];
            const rightEyeIndices = [473, 474, 475, 476, 477];
            
            const getCenter = (indices: number[]) => {
                let sumX = 0, sumY = 0;
                for (let idx of indices) {
                    sumX += landmarks[idx].x;
                    sumY += landmarks[idx].y;
                }
                return {
                    x: sumX / indices.length,
                    y: sumY / indices.length
                };
            };
            
            this.leftIris = getCenter(leftEyeIndices);
            this.rightIris = getCenter(rightEyeIndices);
            this.leftInner = landmarks[133];
            this.rightInner = landmarks[362];
            
            let minX = 1, minY = 1, maxX = 0, maxY = 0;
            for (let lm of landmarks) {
                if (lm.x < minX) minX = lm.x;
                if (lm.y < minY) minY = lm.y;
                if (lm.x > maxX) maxX = lm.x;
                if (lm.y > maxY) maxY = lm.y;
            }
            const faceWidth = maxX - minX;
            const faceHeight = maxY - minY;
            
            const foreheadLeft = minX - faceWidth * 0.35;
            const foreheadRight = maxX + faceWidth * 0.35;
            const foreheadTop = minY - faceHeight * 0.35;
            const foreheadBottom = minY + faceHeight * 0.30;
            
            let isFaceStraight = false;
            const noseTip = landmarks[1];
            const leftCheek = landmarks[234];
            const rightCheek = landmarks[454];
            if (noseTip && leftCheek && rightCheek) {
                const leftDist = Math.abs(noseTip.x - leftCheek.x);
                const rightDist = Math.abs(noseTip.x - rightCheek.x);
                const yawRatio = Math.min(leftDist, rightDist) / Math.max(leftDist, rightDist);
                
                const leftEye = landmarks[159];
                const rightEye = landmarks[386];
                const avgEyeY = (leftEye.y + rightEye.y) / 2;
                const verticalDist = noseTip.y - avgEyeY;
                
                // Relaxed vertical distance (allow users sitting slightly further away)
                if (yawRatio >= 0.65 && verticalDist <= 0.25) {
                    isFaceStraight = true;
                }
            }
            this.isFaceStraight = isFaceStraight;
            
            if (this.cardDistance !== null) {
                const cx = (this.cardDistance.minX + this.cardDistance.maxX) / 2;
                const cy = (this.cardDistance.minY + this.cardDistance.maxY) / 2;
                this.foreheadDetected = (
                    cx >= foreheadLeft &&
                    cx <= foreheadRight &&
                    cy >= foreheadTop &&
                    cy <= foreheadBottom
                );
                
                if (this.foreheadDetected) {
                    this.foreheadDetected = isFaceStraight;
                }
            } else {
                this.foreheadDetected = false;
            }
        } else {
            this.leftIris = null;
            this.rightIris = null;
            this.leftInner = null;
            this.rightInner = null;
            this.foreheadDetected = false;
            this.isFaceStraight = false;
        }
    }
    
    private emitState() {
        const newState = {
            phase: this.phase,
            isFaceStraight: this.isFaceStraight,
            calibrationProgress: this.phase === 'calibration' ? this.calibrationFrameCount : this.pdSamples.length,
            cardDetected: this.lastCardWidth !== undefined
        };
        const stateStr = JSON.stringify(newState);
        if (stateStr !== this.lastEmittedStateStr) {
            this.lastEmittedStateStr = stateStr;
            this.config.onStateChange?.(newState);
        }
    }

    private async detectLoop() {
        if (!this.isRunning) return;

        try {
            if (this.video.readyState === 4 && this.session) {
                // Run MediaPipe on every frame
                await this.faceMesh.send({ image: this.video });
                
                const vw = this.video.videoWidth;
                const vh = this.video.videoHeight;
                
                // --- PHASE 1: CALIBRATION ---
                if (this.phase === 'calibration') {
                    const modelSize = this.YOLO_SIZE;
                    const scale = Math.min(modelSize / vw, modelSize / vh);
                    const drawW = vw * scale;
                    const drawH = vh * scale;
                    const padX = (modelSize - drawW) / 2;
                    const padY = (modelSize - drawH) / 2;

                    this.ctx.save();
                    this.ctx.fillStyle = '#000000';
                    this.ctx.fillRect(0, 0, modelSize, modelSize);
                    this.ctx.translate(modelSize, 0);
                    this.ctx.scale(-1, 1);
                    this.ctx.drawImage(this.video, padX, padY, drawW, drawH);
                    this.ctx.restore();

                    const imageData = this.ctx.getImageData(0, 0, modelSize, modelSize);
                    const data = imageData.data;

                    const redArray = new Float32Array(modelSize * modelSize);
                    const greenArray = new Float32Array(modelSize * modelSize);
                    const blueArray = new Float32Array(modelSize * modelSize);

                    for (let i = 0; i < data.length; i += 4) {
                        redArray[i / 4] = data[i] / 255.0;
                        greenArray[i / 4] = data[i + 1] / 255.0;
                        blueArray[i / 4] = data[i + 2] / 255.0;
                    }

                    const transposedData = new Float32Array(3 * modelSize * modelSize);
                    transposedData.set(redArray, 0);
                    transposedData.set(greenArray, modelSize * modelSize);
                    transposedData.set(blueArray, 2 * modelSize * modelSize);

                    const ort = (window as any).ort;
                    const tensor = new ort.Tensor('float32', transposedData, [1, 3, modelSize, modelSize]);
                    const results = await this.session.run({ images: tensor });
                    const output = results[this.session.outputNames[0]].data as Float32Array;

                    const numBoxes = modelSize === 640 ? 8400 : 18900;
                    const channels = output.length / numBoxes;
                    let boxes = [];

                    for (let i = 0; i < numBoxes; i++) {
                        const cx = output[i];
                        const cy = output[numBoxes + i];
                        const w = output[2 * numBoxes + i];
                        const h = output[3 * numBoxes + i];

                        let maxScore = 0;
                        let classId = -1;
                        
                        for (let c = 4; c < channels; c++) {
                            const score = output[c * numBoxes + i];
                            if (score > maxScore) {
                                maxScore = score;
                                classId = c - 4;
                            }
                        }

                        if (maxScore > 0.01) {
                            boxes.push({
                                classId,
                                score: maxScore,
                                x1: cx - w / 2,
                                y1: cy - h / 2,
                                x2: cx + w / 2,
                                y2: cy + h / 2,
                                w, h
                            });
                        }
                    }

                    boxes = this.nonMaxSuppression(boxes, 0.45);

                    const targetClassId = channels >= 7 ? 2 : 0;
                    const cardBoxes = boxes.filter(b => b.classId === targetClassId || b.classId === 1 || b.classId === 2);
                    let bestCard: any = null;

                    for (let box of cardBoxes) {
                        const boxW = box.x2 - box.x1;
                        const boxH = box.y2 - box.y1;
                        const ratio = Math.max(boxW, boxH) / Math.min(boxW, boxH);
                        
                        if (ratio > 1.25 && ratio < 2.4 && boxW * boxH > vw * vh * 0.01) {
                            if (!bestCard || box.score > bestCard.score) {
                                bestCard = box;
                            }
                        }
                    }

                    if (bestCard) {
                        const toVideoX = (x: number) => (modelSize - x - padX) / scale;
                        const toVideoY = (y: number) => (y - padY) / scale;

                        const x1Mapped = toVideoX(bestCard.x2);
                        const x2Mapped = toVideoX(bestCard.x1);
                        const y1Mapped = toVideoY(bestCard.y1);
                        const y2Mapped = toVideoY(bestCard.y2);

                        this.cardDistance = {
                            minX: x1Mapped / vw,
                            minY: y1Mapped / vh,
                            maxX: x2Mapped / vw,
                            maxY: y2Mapped / vh,
                            videoWidthPixels: x2Mapped - x1Mapped
                        };

                        if (this.foreheadDetected) {
                            const cardWidth = this.cardDistance.videoWidthPixels!;
                            if (this.lastCardWidth !== undefined) {
                                if (Math.abs(cardWidth - this.lastCardWidth) > this.lastCardWidth * 0.15) {
                                    this.calibrationFrameCount = 0;
                                    this.lastCardWidth = cardWidth;
                                    this.cardWidthSamples = [];
                                } else {
                                    this.lastCardWidth = cardWidth;
                                    this.cardWidthSamples.push(cardWidth);
                                    this.calibrationFrameCount++;
                                    this.config.onStatus(`Card Calibration: Hold steady... ${this.calibrationFrameCount}/5`);
                                    
                                    if (this.calibrationFrameCount >= 5) {
                                        // Move to measurement phase
                                        const medianWidth = this.getMedian(this.cardWidthSamples);
                                        this.mmPerPixel = this.selectedCardWidth / medianWidth;
                                        this.phase = 'measurement';
                                        this.config.onStatus(`Measuring PD: Hold steady while we take final measurements... 0/30`);
                                    }
                                }
                            } else {
                                this.lastCardWidth = cardWidth;
                                this.cardWidthSamples.push(cardWidth);
                                this.calibrationFrameCount++;
                                this.config.onStatus(`Card Calibration: Hold steady... ${this.calibrationFrameCount}/5`);
                            }
                        } else {
                            this.calibrationFrameCount = 0;
                            this.lastCardWidth = undefined;
                            this.cardWidthSamples = [];
                            this.config.onStatus("Card Calibration: Sit straight, center your face, and hold card steady on forehead.");
                        }
                    } else {
                        this.calibrationFrameCount = 0;
                        this.lastCardWidth = undefined;
                        this.cardWidthSamples = [];
                        this.cardDistance = null;
                        this.config.onStatus("Card Calibration: Sit straight, center your face, and hold card steady on forehead.");
                    }
                } 
                // --- PHASE 2: MEASUREMENT ---
                else if (this.phase === 'measurement') {
                    if (this.leftIris && this.rightIris && this.leftInner && this.rightInner && this.isFaceStraight && this.mmPerPixel !== null) {
                        const leftX = this.leftIris.x * vw;
                        const leftY = this.leftIris.y * vh;
                        const rightX = this.rightIris.x * vw;
                        const rightY = this.rightIris.y * vh;
                        
                        const dx = rightX - leftX;
                        const dy = rightY - leftY;
                        const irisDistancePixels = Math.sqrt(dx * dx + dy * dy);
                        const totalPD = irisDistancePixels * this.mmPerPixel;
                        
                        if (totalPD >= 35 && totalPD <= 90) {
                            let sample: { total: number, left?: number, right?: number } = { total: totalPD };
                            
                            if (this.config.pdType === 'dual') {
                                const leftInnerX = this.leftInner.x * vw;
                                const rightInnerX = this.rightInner.x * vw;
                                const midlineX = (leftInnerX + rightInnerX) / 2;
                                
                                let leftPixels = Math.abs(leftX - midlineX);
                                let rightPixels = Math.abs(rightX - midlineX);
                                
                                let leftPD = leftPixels * this.mmPerPixel;
                                let rightPD = rightPixels * this.mmPerPixel;
                                
                                const splitSum = leftPD + rightPD;
                                if (splitSum > 0) {
                                    const normalization = totalPD / splitSum;
                                    leftPD *= normalization;
                                    rightPD *= normalization;
                                    
                                    if (leftPD >= 15 && leftPD <= 50 && rightPD >= 15 && rightPD <= 50) {
                                        sample.left = leftPD;
                                        sample.right = rightPD;
                                    } else {
                                        // Skip invalid dual samples
                                    }
                                }
                            }
                            
                            if (!this.config.pdType || this.config.pdType === 'single' || sample.left !== undefined) {
                                this.pdSamples.push(sample);
                                this.config.onStatus(`Measuring PD: Hold steady while we take final measurements... ${this.pdSamples.length}/30`);
                                
                                if (this.pdSamples.length >= 30) {
                                    this.isRunning = false;
                                    
                                    const totalVals = this.pdSamples.map(s => s.total).sort((a,b) => a-b);
                                    const totalMedian = totalVals.length % 2 !== 0 
                                        ? totalVals[Math.floor(totalVals.length/2)] 
                                        : (totalVals[Math.floor(totalVals.length/2)-1] + totalVals[Math.floor(totalVals.length/2)])/2;
                                        
                                    const filtered = this.pdSamples.filter(s => Math.abs(s.total - totalMedian) <= 2.0);
                                    if (filtered.length === 0) {
                                        this.config.onError("Measurement failed. Please try again.");
                                        return;
                                    }
                                    
                                    const avgTotal = filtered.reduce((sum, s) => sum + s.total, 0) / filtered.length;
                                    let finalTotal = Math.round(avgTotal * 2) / 2;
                                    
                                    if (this.config.pdType === 'dual') {
                                        const avgLeft = filtered.reduce((sum, s) => sum + s.left!, 0) / filtered.length;
                                        const avgRight = filtered.reduce((sum, s) => sum + s.right!, 0) / filtered.length;
                                        
                                        let finalLeft = Math.round(avgLeft * 2) / 2;
                                        let finalRight = Math.round(avgRight * 2) / 2;
                                        
                                        finalRight = Math.round((finalTotal - finalLeft) * 2) / 2;
                                        
                                        this.config.onDetected({
                                            pd: finalTotal,
                                            leftPd: finalLeft,
                                            rightPd: finalRight
                                        });
                                    } else {
                                        this.config.onDetected({ pd: finalTotal });
                                    }
                                    return;
                                }
                            }
                        }
                    } else {
                        this.config.onStatus("Sit straight and keep your face centered");
                    }
                }
            }
        } catch (err: any) {
            console.error("detectLoop error:", err);
            this.config.onError("Loop error: " + err.message);
            this.isRunning = false;
        }
        
        if (this.isRunning) {
            this.emitState();
            requestAnimationFrame(this.detectLoop.bind(this));
        }
    }
    
    private getMedian(arr: number[]): number {
        const sorted = [...arr].sort((a, b) => a - b);
        const mid = Math.floor(sorted.length / 2);
        return sorted.length % 2 !== 0 
            ? sorted[mid] 
            : (sorted[mid - 1] + sorted[mid]) / 2;
    }
    
    private nonMaxSuppression(boxes: any[], iouThreshold: number) {
        boxes.sort((a, b) => b.score - a.score);
        const selected = [];
        const active = new Array(boxes.length).fill(true);
        
        for (let i = 0; i < boxes.length; i++) {
            if (active[i]) {
                const boxA = boxes[i];
                selected.push(boxA);
                for (let j = i + 1; j < boxes.length; j++) {
                    if (active[j]) {
                        const boxB = boxes[j];
                        if (this.computeIoU(boxA, boxB) > iouThreshold) {
                            active[j] = false;
                        }
                    }
                }
            }
        }
        return selected;
    }
    
    private computeIoU(box1: any, box2: any) {
        const x1 = Math.max(box1.x1, box2.x1);
        const y1 = Math.max(box1.y1, box2.y1);
        const x2 = Math.min(box1.x2, box2.x2);
        const y2 = Math.min(box1.y2, box2.y2);
        
        const intersection = Math.max(0, x2 - x1) * Math.max(0, y2 - y1);
        const area1 = (box1.x2 - box1.x1) * (box1.y2 - box1.y1);
        const area2 = (box2.x2 - box2.x1) * (box2.y2 - box2.y1);
        const union = area1 + area2 - intersection;
        
        return intersection / union;
    }
}
